import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Mail, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { adminChangePassword, adminChangeEmail } from '../api/api';

export const AdminProfile = () => {
  const { user, logout } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    // Doit correspondre à la règle du backend (PasswordChangeRequest) :
    // 10 caractères minimum, avec au moins une lettre et un chiffre.
    const mdp = passwordForm.newPassword;
    if (mdp.length < 10 || !/[A-Za-z]/.test(mdp) || !/\d/.test(mdp)) {
      toast.error('Le mot de passe doit contenir au moins 10 caractères, dont des lettres et des chiffres');
      return;
    }

    setIsChangingPassword(true);

    try {
      await adminChangePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Mot de passe modifié avec succès ! Reconnectez-vous.');
      
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Déconnecter après 2 secondes
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la modification';
      toast.error(errorMsg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailForm.newEmail.includes('@')) {
      toast.error('Email invalide');
      return;
    }

    setIsChangingEmail(true);

    try {
      await adminChangeEmail(emailForm.newEmail, emailForm.password);
      toast.success('Email modifié avec succès ! Reconnectez-vous avec votre nouvel email.');
      
      setEmailForm({
        newEmail: '',
        password: ''
      });
      
      // Déconnecter après 2 secondes
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la modification';
      toast.error(errorMsg);
    } finally {
      setIsChangingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Mon profil</h2>
        <p className="text-gray-600">Gérez vos informations de connexion</p>
      </div>

      {/* Current Info */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-gray-900">Compte administrateur</h3>
              <p className="text-gray-600 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                {user?.email}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Form */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Lock className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Changer le mot de passe</h3>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Mot de passe actuel *</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Nouveau mot de passe *</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                required
                className="h-12"
                minLength={10}
              />
              <p className="text-sm text-gray-500">Au moins 10 caractères, avec lettres et chiffres</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
                className="h-12"
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={isChangingPassword}
                className="bg-blue-600 hover:bg-blue-700 px-8"
              >
                {isChangingPassword ? (
                  <span className="flex items-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Modification...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    Changer le mot de passe
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Change Email Form */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Mail className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-bold text-gray-900">Changer l'email</h3>
          </div>
          
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newEmail">Nouvel email *</Label>
              <Input
                id="newEmail"
                type="email"
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                required
                className="h-12"
                placeholder="nouveau@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailPassword">Mot de passe actuel (pour confirmation) *</Label>
              <Input
                id="emailPassword"
                type="password"
                value={emailForm.password}
                onChange={(e) => setEmailForm(prev => ({ ...prev, password: e.target.value }))}
                required
                className="h-12"
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={isChangingEmail}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 px-8"
              >
                {isChangingEmail ? (
                  <span className="flex items-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Modification...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    Changer l'email
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card className="border border-gray-200 shadow-lg bg-white">
        <CardContent className="p-6">
          <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-gray-900 mb-3"><Lock className="w-5 h-5 text-blue-600" aria-hidden="true" /> Sécurité</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Après avoir changé votre mot de passe ou email, vous serez automatiquement déconnecté</li>
            <li>• Reconnectez-vous avec vos nouvelles informations</li>
            <li>• Utilisez un mot de passe fort (minimum 8 caractères)</li>
            <li>• Ne partagez jamais vos identifiants</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
